import { type FC, type ChangeEvent, useState } from "react";

import { FieldError } from "@/shared/components/ui/FieldError";
import { Input } from "@/shared/components/ui/input";
import { cn } from "@/shared/lib/utils";

interface YearInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  minYear?: number;
}

function getCurrentYear() {
  return new Date().getFullYear();
}

export const YearInput: FC<YearInputProps> = ({
  value,
  onChange,
  placeholder = "YYYY",
  className,
  disabled,
  minYear = 1900,
}) => {
  const [error, setError] = useState("");

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 4);
    setError("");
    onChange(raw);
  };

  const handleBlur = () => {
    if (!value) {
      setError("");
      return;
    }
    const num = Number(value);
    if (value.length < 4) {
      setError("Enter a 4-digit year");
    } else if (num > getCurrentYear()) {
      setError(`Year cannot exceed ${getCurrentYear()}`);
    } else if (num < minYear) {
      setError(`Year must be ${minYear} or later`);
    } else {
      setError("");
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <Input
        inputMode="numeric"
        maxLength={4}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        className={cn(error && "border-destructive", className)}
      />
      <FieldError message={error || undefined} />
    </div>
  );
};
